import React from 'react'
import CheckOutlineIcon from 'transfix-icons/build/CheckOutlineIcon'
import ClearIcon from 'transfix-icons/build/ClearIcon'
import { makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { Maybe } from 'types/graphqlTypes'
import { MergeDetailsListItem } from './MergeDetailsListItem'
import { SelectedFacilityAddress } from './SelectedFacilityAddress'
import { TSelectedFacility } from './constants'

const useMergeDetailsStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  bold: {
    fontWeight: 600,
  },
  detailsList: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: theme.spacing(1.5),
    padding: theme.spacing(0.5, 0),
  },
  detailsColumnInner: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
  selectedFacility: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    borderTop: `${brandColors.coolGray3} solid 1px`,
    paddingTop: theme.spacing(2),
  },
  warning: {
    color: brandColors.warning1,
  },
}))

interface IMergeDetailsProps {
  facility: Maybe<TSelectedFacility>
}

export const MergeDetails = ({ facility }: IMergeDetailsProps) => {
  const classes = useMergeDetailsStyles()
  return (
    <div className={classes.root} data-test='mergeDetails'>
      <Typography variant='body1'>
        Merging this facility will create an archive record of it in the primary facility and all
        facility data will become read only.
      </Typography>
      <div className={classes.detailsList}>
        <div className={classes.detailsColumnInner}>
          <Typography variant='body1' className={classes.bold}>
            The following WILL NOT be merged into the primary facility:
          </Typography>
          <Typography variant='caption'>(Will use primary facility instead)</Typography>
          <div className={classes.detailsColumnInner}>
            <MergeDetailsListItem
              Icon={ClearIcon}
              iconColor='error1'
              label='Any matching contact names'
            />
            <MergeDetailsListItem
              Icon={ClearIcon}
              iconColor='error1'
              label='Any matching shipper facilities'
            />
            <MergeDetailsListItem
              Icon={ClearIcon}
              iconColor='error1'
              label='Any matching vendors'
            />
            <MergeDetailsListItem
              Icon={ClearIcon}
              iconColor='error1'
              label='Any facility overview details'
            />
            <MergeDetailsListItem Icon={ClearIcon} iconColor='error1' label='Any facility hours' />
            <MergeDetailsListItem
              Icon={ClearIcon}
              iconColor='error1'
              label='Any scheduling details'
            />
            <MergeDetailsListItem
              Icon={ClearIcon}
              iconColor='error1'
              label='Any additional details'
            />
          </div>
        </div>
        <div className={classes.detailsColumnInner}>
          <Typography variant='body1' className={classes.bold}>
            The following WILL be merged into the primary facility:
          </Typography>
          <Typography variant='caption'>(Will add to the primary facility)</Typography>
          <div className={classes.detailsColumnInner}>
            <MergeDetailsListItem
              Icon={CheckOutlineIcon}
              iconColor='green3'
              iconSize='medium'
              label='Any new or differing contact names, contact methods: emails or phone numbers'
            />
            <MergeDetailsListItem
              Icon={CheckOutlineIcon}
              iconColor='green3'
              iconSize='medium'
              label='Any new or differing shipper facilities & EDI location codes'
            />
            <MergeDetailsListItem
              Icon={CheckOutlineIcon}
              iconColor='green3'
              iconSize='medium'
              label='Any new or differing vendors'
            />
          </div>
        </div>
      </div>
      <div className={classes.selectedFacility}>
        <Typography variant='body2'>Merging facility into:</Typography>
        <SelectedFacilityAddress facility={facility} />
      </div>
    </div>
  )
}
